import React, { useMemo, useState } from 'react'
import {
  DetailCard,
  DetailCardGrid,
  DETAIL_CARD_DEMO_ROWS,
  DETAIL_CARD_FAMILY_ROWS,
  DETAIL_CARD_PREVIEW_STATE_LABELS,
  DETAIL_CARD_PREVIEW_STATES,
  type DetailCardPreviewState,
  type DetailCardVariant,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './DetailCardShowcase.css'

const DetailCardShowcase: React.FC = () => {
  const [title, setTitle] = useState('Individual')
  const [variant, setVariant] = useState<DetailCardVariant>('plain')
  const [darkMode, setDarkMode] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<DetailCardPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'detail-card-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="detail-card-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as DetailCardPreviewState,
                  )
                }
              >
                {DETAIL_CARD_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {DETAIL_CARD_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'detail-card-title',
            label: 'Title',
            control: (
              <input
                id="detail-card-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'detail-card-variant',
            label: 'Variant',
            control: (
              <select
                id="detail-card-variant"
                className="ui-field-control"
                value={variant}
                onChange={(event) =>
                  setVariant(event.target.value as DetailCardVariant)
                }
              >
                <option value="plain">Plain</option>
                <option value="tinted">Tinted</option>
              </select>
            ),
          },
          {
            id: 'detail-card-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="detail-card-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedState, title, variant],
  )

  const liveVariant: DetailCardVariant =
    highlightedState === 'tinted' ? 'tinted' : variant

  const liveTitle =
    highlightedState === 'family'
      ? 'Family'
      : highlightedState !== 'default'
        ? undefined
        : title

  return (
    <>
      <UiPreviewCanvas
        title="Detail card"
        description="Title plus label/value rows for plan limits and similar summaries — as used in Patient Profile → Insurance Summary (Individual / Family)."
        stageClassName="ui-preview__stage--wide detail-card-showcase__stage-wrap"
      >
        <div
          className={`detail-card-showcase__sheet${
            darkMode ? ' detail-card-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Controls drive the live card. Highlighted state overrides copy for
              matrix samples.
            </p>
            <div
              className={`detail-card-showcase__live-panel${
                liveVariant === 'tinted'
                  ? ' detail-card-showcase__live-panel--tinted'
                  : ''
              }${darkMode ? ' detail-card-showcase__live-panel--dark' : ''}`}
            >
              <DetailCard
                title={liveTitle}
                rows={
                  highlightedState === 'default'
                    ? DETAIL_CARD_DEMO_ROWS
                    : undefined
                }
                variant={liveVariant}
                darkMode={darkMode}
                previewState={
                  highlightedState === 'default' ? undefined : highlightedState
                }
              />
            </div>
          </section>

          <section className="detail-card-showcase__demo">
            <h3 className="ui-showcase-live__title">
              Insurance Summary — Plan overall
            </h3>
            <p className="ui-showcase-live__note">
              Plain pair for deductible & annual maximum (Individual / Family).
            </p>
            <div
              className={`detail-card-showcase__panel${
                darkMode ? ' detail-card-showcase__panel--dark' : ''
              }`}
            >
              <DetailCardGrid>
                <DetailCard
                  title="Individual"
                  rows={DETAIL_CARD_DEMO_ROWS}
                  darkMode={darkMode}
                />
                <DetailCard
                  title="Family"
                  rows={DETAIL_CARD_FAMILY_ROWS}
                  darkMode={darkMode}
                />
              </DetailCardGrid>
            </div>
          </section>

          <section className="detail-card-showcase__demo">
            <h3 className="ui-showcase-live__title">
              Category-specific limits
            </h3>
            <p className="ui-showcase-live__note">
              Tinted cards nested under a category name (e.g. Orthodontics).
            </p>
            <div
              className={`detail-card-showcase__category${
                darkMode ? ' detail-card-showcase__category--dark' : ''
              }`}
            >
              <div className="detail-card-showcase__category-name">
                Orthodontics
              </div>
              <DetailCardGrid>
                <DetailCard
                  title="Individual"
                  variant="tinted"
                  darkMode={darkMode}
                  rows={[
                    {
                      label: 'Lifetime maximum',
                      value: '$800.00 / $1,000.00',
                    },
                    {
                      label: 'Remaining',
                      value: '$200.00',
                      highlight: true,
                    },
                  ]}
                />
                <DetailCard
                  title="Family"
                  variant="tinted"
                  darkMode={darkMode}
                  rows={[
                    {
                      label: 'Lifetime maximum',
                      value: 'None',
                      muted: true,
                    },
                  ]}
                />
              </DetailCardGrid>
            </div>
          </section>

          <section className="detail-card-showcase__matrix">
            {DETAIL_CARD_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`detail-card-showcase__row${
                  highlightedState === previewState
                    ? ' detail-card-showcase__row--selected'
                    : ''
                }${darkMode ? ' detail-card-showcase__row--dark' : ''}`}
              >
                <h4 className="detail-card-showcase__label">
                  {DETAIL_CARD_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <DetailCard previewState={previewState} darkMode={darkMode} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="DetailCard"
        sections={propertySections}
      />
    </>
  )
}

export default DetailCardShowcase
